import React, { useState, useEffect } from "react";
import myAxios from "../AxiosUrl";
import "./Logs.css";
import Loading from "../Loading/Loading";
import { getAdminPermissions } from "../../utils/adminPermissions";

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  // Filters
  const [selectedType, setSelectedType] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Statistics
  const [stats, setStats] = useState({
    totalLogs: 0,
    endpointLogs: 0,
    errorLogs: 0,
    adminLogs: 0,
    userLogs: 0,
    todayLogs: 0,
  });

  // Active tab
  const [activeTab, setActiveTab] = useState("system");

  // Modal state
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { isSuperAdmin } = getAdminPermissions();
  const canDeleteCurrentTab = activeTab === "system" || isSuperAdmin;

  const itemsPerPage = 10;

  useEffect(() => {
    if (activeTab === "system") {
      fetchSystemLogs();
    } else {
      fetchErrorLogs();
    }
    fetchLogStats();
  }, [
    currentPage,
    selectedType,
    selectedDate,
    selectedUserId,
    searchTerm,
    activeTab,
  ]);

  const fetchSystemLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(selectedType !== "all" && { type: selectedType }),
        ...(selectedDate && { date: selectedDate }),
        ...(selectedUserId && { userId: selectedUserId }),
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await myAxios.get(`/logs?${params}`);

      if (response.data.success) {
        setLogs(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalLogs(response.data.totalCount);
        setError(null);
      } else {
        setError("Failed to fetch logs");
      }
    } catch (err) {
      setError("Error fetching logs");
      console.error("Error fetching logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchErrorLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(selectedDate && { date: selectedDate }),
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await myAxios.get(`/error-logs?${params}`);

      if (response.data.success) {
        setErrorLogs(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotalLogs(response.data.totalCount);
        setError(null);
      } else {
        setError("Failed to fetch error logs");
      }
    } catch (err) {
      setError("Error fetching error logs");
      console.error("Error fetching error logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogStats = async () => {
    try {
      const response = await myAxios.get("/logs/stats");

      if (response.data.success) {
        const data = response.data.data;
        setStats({
          totalLogs: data.totalLogs || 0,
          endpointLogs: data.logsByType?.endpoint || 0,
          errorLogs: data.logsByType?.error || 0,
          adminLogs: data.logsByType?.admin || 0,
          userLogs: data.logsByType?.user || 0,
          todayLogs: data.todayLogs || 0,
        });
      }
    } catch (err) {
      console.error("Error fetching log stats:", err);
    }
  };

  const handleDeleteAllLogs = async () => {
    if (activeTab === "error" && !isSuperAdmin) {
      setError("Only super admins can clear error logs");
      setShowDeleteConfirm(false);
      return;
    }

    setDeleting(true);
    try {
      const endpoint = activeTab === "system" ? "/logs" : "/error-logs";
      const response = await myAxios.delete(endpoint);

      if (response.data.success) {
        if (activeTab === "system") {
          setLogs([]);
        } else {
          setErrorLogs([]);
        }
        setTotalLogs(0);
        setTotalPages(1);
        setCurrentPage(1);
        fetchLogStats();
      } else {
        setError("Failed to delete logs");
      }
    } catch (err) {
      setError("Error deleting logs");
      console.error("Error deleting logs:", err);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleLogClick = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedType("all");
    setSelectedDate("");
    setSelectedUserId("");
    setSearchTerm("");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return "success";
    if (statusCode >= 400 && statusCode < 500) return "warning";
    if (statusCode >= 500) return "error";
    return "default";
  };

  const getLogTypeColor = (type) => {
    switch (type) {
      case "endpoint":
        return "primary";
      case "error":
        return "error";
      case "admin":
        return "warning";
      case "user":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <div className="logs-container">
      <div className="logs-header">
        <h1>System Logs</h1>
        <div className="logs-actions">
          <button
            className="btn btn-danger"
            onClick={() => {
              if (!canDeleteCurrentTab) {
                setError("Only super admins can clear error logs");
                return;
              }
              setShowDeleteConfirm(true);
            }}
            disabled={!canDeleteCurrentTab}
            title={
              canDeleteCurrentTab
                ? "Delete all logs"
                : "Super admin permission required"
            }
          >
            Clear All Logs
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <span className="material-icons">assessment</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {stats.totalLogs.toLocaleString()}
            </div>
            <div className="stat-label">Total Logs</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <span className="material-icons">http</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {stats.endpointLogs.toLocaleString()}
            </div>
            <div className="stat-label">Endpoint Logs</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <span className="material-icons">error</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {stats.errorLogs.toLocaleString()}
            </div>
            <div className="stat-label">Error Logs</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <span className="material-icons">today</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {stats.todayLogs.toLocaleString()}
            </div>
            <div className="stat-label">Today's Logs</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="logs-tabs">
        <button
          className={`tab-btn ${activeTab === "system" ? "active" : ""}`}
          onClick={() => handleTabChange("system")}
        >
          System Logs
        </button>
        <button
          className={`tab-btn ${activeTab === "error" ? "active" : ""}`}
          onClick={() => handleTabChange("error")}
        >
          Error Logs
        </button>
      </div>

      {/* Filters */}
      <div className="logs-filters">
        <div className="filter-group">
          <label>Type:</label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            disabled={activeTab !== "system"}
          >
            <option value="all">All Types</option>
            <option value="endpoint">Endpoint</option>
            <option value="error">Error</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Date:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>User ID:</label>
          <input
            type="text"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            placeholder="Enter user ID"
            disabled={activeTab !== "system"}
          />
        </div>

        <div className="filter-group">
          <label>Search:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search logs..."
          />
        </div>
      </div>

      {/* Logs Table */}
      <div className="logs-table-container">
        {loading ? (
          <Loading />
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <table className="logs-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Type</th>
                {activeTab === "system" && <th>User</th>}
                {activeTab === "system" && <th>Endpoint</th>}
                {activeTab === "system" && <th>Status</th>}
                {activeTab === "error" && <th>Level</th>}
                <th>Message</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeTab === "system"
                ? logs.map((log, index) => (
                    <tr key={index}>
                      <td>{formatDate(log.timestamp)}</td>
                      <td>
                        <span
                          className={`log-type ${getLogTypeColor(log.type)}`}
                        >
                          {log.type}
                        </span>
                      </td>
                      <td>{log.userName || log.userId || "Anonymous"}</td>
                      <td>{log.endpoint}</td>
                      <td>
                        <span
                          className={`status-code ${getStatusColor(
                            log.statusCode
                          )}`}
                        >
                          {log.statusCode}
                        </span>
                      </td>
                      <td className="log-message">{log.message}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleLogClick(log)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                : errorLogs.map((log, index) => (
                    <tr key={index}>
                      <td>{formatDate(log.timestamp)}</td>
                      <td>
                        <span className="log-type error">Error</span>
                      </td>
                      <td>{log.level}</td>
                      <td className="log-message">{log.message}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleLogClick(log)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="btn btn-secondary"
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {totalPages} ({totalLogs} total logs)
        </span>
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="btn btn-secondary"
        >
          Next
        </button>
      </div>

      {/* Log Details Modal */}
      {showModal && selectedLog && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Log Details</h2>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="log-details">
                <div className="detail-row">
                  <strong>Timestamp:</strong>{" "}
                  {formatDate(selectedLog.timestamp)}
                </div>
                <div className="detail-row">
                  <strong>Type:</strong> {selectedLog.type}
                </div>
                {selectedLog.userId && (
                  <div className="detail-row">
                    <strong>User ID:</strong> {selectedLog.userId}
                  </div>
                )}
                {selectedLog.userName && (
                  <div className="detail-row">
                    <strong>User Name:</strong> {selectedLog.userName}
                  </div>
                )}
                {selectedLog.endpoint && (
                  <div className="detail-row">
                    <strong>Endpoint:</strong> {selectedLog.endpoint}
                  </div>
                )}
                {selectedLog.statusCode && (
                  <div className="detail-row">
                    <strong>Status Code:</strong> {selectedLog.statusCode}
                  </div>
                )}
                {selectedLog.method && (
                  <div className="detail-row">
                    <strong>Method:</strong> {selectedLog.method}
                  </div>
                )}
                {selectedLog.ip && (
                  <div className="detail-row">
                    <strong>IP Address:</strong> {selectedLog.ip}
                  </div>
                )}
                {selectedLog.userAgent && (
                  <div className="detail-row">
                    <strong>User Agent:</strong> {selectedLog.userAgent}
                  </div>
                )}
                <div className="detail-row">
                  <strong>Message:</strong> {selectedLog.message}
                </div>
                {selectedLog.details && (
                  <div className="detail-row">
                    <strong>Details:</strong>
                    <pre>{JSON.stringify(selectedLog.details, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete all {activeTab} logs?</p>
              <p className="warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteAllLogs}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete All"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logs;
